// Define a type for API environment options

import type { ComponentType, SVGProps } from 'react';

import LucideCircleCheck from '@/icons/component/LucideCircleCheck';
import LucideInfo from '@/icons/component/LucideInfo';
import LucideTriangleAlert from '@/icons/component/LucideTriangleAlert';
import { WebAppEnvironment } from '@/types/api';
import { currentConfigName } from '@/utils/firebase-config';
import { getJWTCookieServer } from '@/utils/firebase-helpers';
import {
  getTargetEnvironmentServer,
} from '@/utils/target-detection';

import {
  getActiveSpaceServer,
} from './firebaseServices/AuthServices/helpers';
import type { AsyncResult, ServiceConfig } from './httpServices/types';

// Define API types
export enum ApiTypes {
  IDA = 'ida',
  EDITOR = 'editor',
  QUIZ_GENERATION = 'quizGeneration',
}

type ApiType = (typeof ApiTypes)[keyof typeof ApiTypes];

// Map each environment to its respective API base URLs
const apiBaseUrls: Record<ApiType, Record<WebAppEnvironment, string>> = {
  ida: {
    production: 'https://ida.production.howfm.io',
    staging: 'https://ida.develop.howfm.io',
    samwise: 'https://ida.samwise.howfm.io',
    merry: 'https://ida.merry.howfm.io',
    bilbo: 'https://ida.bilbo.howfm.io',
    pippin: 'https://ida.pippin.howfm.io',
    frodo: 'https://ida.frodo.howfm.io',
  },
  editor: {
    production: 'https://api.editor.production.howfm.io',
    staging: 'https://api.editor.develop.howfm.io',
    samwise: 'https://api.editor.samwise.howfm.io',
    merry: 'https://api.editor.merry.howfm.io',
    bilbo: 'https://api.editor.bilbo.howfm.io',
    pippin: 'https://api.editor.pippin.howfm.io',
    frodo: 'https://api.editor.frodo.howfm.io',
  },
  quizGeneration: {
    production: 'https://quiz-generation-api.production.howfm.io/generate/',
    staging: 'https://quiz-generation-api.develop.howfm.io/generate/',
    samwise: 'https://quiz-generation-api.samwise.howfm.io/generate/',
    merry: 'https://quiz-generation-api.merry.howfm.io/generate/',
    bilbo: 'https://quiz-generation-api.bilbo.howfm.io/generate/',
    pippin: 'https://quiz-generation-api.pippin.howfm.io/generate/',
    frodo: 'https://quiz-generation-api.frodo.howfm.io/generate/',
  },
};

// Updated function with optional apiType parameter
export function getApiBase(apiType: ApiType = ApiTypes.EDITOR, activeTarget: WebAppEnvironment = WebAppEnvironment.STAGING) {
  return apiBaseUrls[apiType][activeTarget];
}

export async function generateHttpRequestConfigServer(): Promise<
  ServiceConfig['httpRequestConfig']
> {
  const [space, target, authToken] = await Promise.all([
    getActiveSpaceServer(),
    getTargetEnvironmentServer(),
    getJWTCookieServer(),
  ]);

  return { space, target, authToken };
}

export async function tryCatch<T, E = Error>(
  promise: Promise<T>,
): Promise<AsyncResult<T, E>> {
  try {
    const data = await promise;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as E };
  }
}

// Environment display configuration
export type EnvironmentDisplayInfo = {
  name: string;
  variant: 'success' | 'warning' | 'info';
  icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const environmentDisplayConfig: Record<string, EnvironmentDisplayInfo> = {
  production: {
    name: 'Production',
    variant: 'success',
    icon: LucideCircleCheck,
  },
  staging: {
    name: 'Staging',
    variant: 'warning',
    icon: LucideTriangleAlert,
  },
  samwise: {
    name: 'Samwise (Dev)',
    variant: 'info',
    icon: LucideInfo,
  },
  frodo: {
    name: 'Frodo (Dev)',
    variant: 'info',
    icon: LucideInfo,
  },
  merry: {
    name: 'Merry (Dev)',
    variant: 'info',
    icon: LucideInfo,
  },
  pippin: {
    name: 'Pippin (Dev)',
    variant: 'info',
    icon: LucideInfo,
  },
  bilbo: {
    name: 'Bilbo (Dev)',
    variant: 'info',
    icon: LucideInfo,
  },
  unknown: {
    name: 'Unknown',
    variant: 'info',
    icon: LucideInfo,
  },
};

// Get display information for an environment
export function getEnvironmentDisplayInfo(targetEnv?: WebAppEnvironment | ''): EnvironmentDisplayInfo {
  const envName = currentConfigName(targetEnv as WebAppEnvironment);
  return environmentDisplayConfig[envName] ?? environmentDisplayConfig.unknown ?? {
    name: 'Unknown',
    variant: 'info' as const,
    icon: LucideInfo,
  };
}
